import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useLazyQuery } from "@apollo/client";
import { COMMON_SETUP_LISTS_QUERY } from "../Queries/SetupListsQuery";
import { UserContext } from "../Contexts/UserContext";
import { GET_ORIS_QUERY } from "../Queries/GetOrisQuery";
import {
  checkLocalStorageAge,
  isSetupMissingProp,
  localGetData,
  localStoreData,
} from "../utils/utils";

import moment from "moment";

export const SetupContext = createContext();

const SetupContextProvider = props => {
  const { user, responseErrorHandler, onlineState } = useContext(UserContext);
  const [setup, setSetup] = useState([]);
  const [specificSetup, setSpecificSetup] = useState([]);
  const [saOris, setSaOris] = useState([]);

  const [getSaOris, { saLoading }] = useLazyQuery(GET_ORIS_QUERY, {
    context: { serializationKey: "incidentQueue" },
    onError: error => {
      console.error(JSON.stringify(error));
    },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        let filtered = data.getOris.filter(o => o.agencyType === "SA");
        window.localStorage.removeItem(`${user?.id}-mp-sa-oris`);
        localStoreData(filtered, `${user?.id}-mp-sa-oris`);
        setSaOris(filtered);
      }
    },
  });

  useEffect(() => {
    if (user?.id && checkLocalStorageAge(`${user?.id}-mp-sa-oris`, 8)) {
      let data = localGetData(`${user?.id}-mp-sa-oris`);
      setSaOris(data);
    } else {
      if (user?.id && !saLoading) {
        getSaOris();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, saLoading]);

  const [getSetup, { loading: setupLoading, error }] = useLazyQuery(
    COMMON_SETUP_LISTS_QUERY,
    {
      context: { serializationKey: "incidentQueue" },
      variables: { oriId: "1", originatorId: user?.id },
      fetchPolicy: "network-only",
      onError: error => {
        console.error(JSON.stringify(error));
      },
      onCompleted: data => {
        if (data) {
          // console.log('Finished fetching common setup list items')
          setSetup([]);
          responseErrorHandler(data);
          localStoreData(data, "setup").then(() => checkForSetup(true, true));
        }
      },
    }
  );

  const checkForSetup = useCallback(
    (stopLoop = false, forceRefresh = false) => {
      const storedSetup = localGetData("setup");
      if (storedSetup) {
        if (!storedSetup?.setupLists) {
          // console.log('Missing setupLists in setup')
          localStorage.removeItem("setup");
          getSetup();
          return false;
        }

        const now = moment();
        const age = now.diff(storedSetup.timestamp, "hours");
        if (
          !stopLoop &&
          (age > 8 || isSetupMissingProp(storedSetup?.setupLists)) &&
          onlineState
        ) {
          // console.log('Setup was too old or was missing a property')
          localStorage.removeItem("setup");
          getSetup();
          return false;
        }

        if (forceRefresh || !setup?.setupLists) {
          // console.log('Setup is fresh. Using localStorage setup for setup state')
          setSetup(storedSetup);
        }

        return true;
      } else {
        // console.log('Did not find setup in local storage')
        getSetup();
        return false;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onlineState, setup?.setupLists]
  );

  let radioLogCodeId = setup?.setupLists?.logCodes.find(lc =>
    lc?.name?.startsWith("43/")
  )?.id;
  let vehicleQueryCode = setup?.setupLists?.logCodes.find(lc =>
    lc?.name?.startsWith("28/")
  )?.id;
  let personQueryCode = setup?.setupLists?.logCodes.find(lc =>
    lc?.name?.startsWith("27/")
  )?.id;

  useEffect(() => {
    // console.log('Firing checkForSetup useEffect')
    checkForSetup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) alert(`There was an error fetching the setup context: ${error}`);

  return (
    <SetupContext.Provider
      value={{
        setup,
        setSetup,
        getSetup,
        saOris,
        setupLoading,
        // commonSetup, setCommonSetup,
        radioLogCodeId,
        vehicleQueryCode,
        personQueryCode,
        specificSetup,
        setSpecificSetup,
        checkForSetup,
      }}
    >
      {props.children}
    </SetupContext.Provider>
  );
};

export default SetupContextProvider;
